import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { X, CalendarDays, Clock, User, Phone, CheckCircle2, Hourglass, Scissors, ChevronLeft, ChevronRight, Users } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const IT_GIORNI_FULL = ["domenica", "lunedì", "martedì", "mercoledì", "giovedì", "venerdì", "sabato"];
const IT_MESI = [
  "gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno",
  "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre",
];
const WEEK_HEADER = ["L", "M", "M", "G", "V", "S", "D"];
const MAX_MONTHS_AHEAD = 5; // hoy + 5 = 6 meses de ventana

const toISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const monthDays = (offset, offDays = new Set()) => {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const count = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  const todayISO = toISO(now);
  const cells = [];
  // Relleno inicial: la semana empieza en lunes
  const lead = (first.getDay() + 6) % 7;
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let d = 1; d <= count; d++) {
    const date = new Date(first.getFullYear(), first.getMonth(), d);
    const iso = toISO(date);
    const wd = date.getDay();
    const backendWd = (wd + 6) % 7; // JS 0=dom -> backend 0=lun
    cells.push({
      iso,
      day: d,
      disabled: iso < todayISO || wd === 0 || wd === 1 || offDays.has(backendWd), // pasado, dom/lun y descansos del barbero: chiuso
    });
  }
  return { first, cells };
};

export default function QuickBook({ onClose }) {
  const [services, setServices] = useState([]);
  const [team, setTeam] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [barbiere, setBarbiere] = useState(""); // "" = il primo disponibile
  const [servizio, setServizio] = useState("");
  const [giorno, setGiorno] = useState("");
  const [slots, setSlots] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [ora, setOra] = useState("");
  const [nome, setNome] = useState(() => localStorage.getItem("bl_qb_nome") || "");
  const [telefono, setTelefono] = useState(() => localStorage.getItem("bl_qb_tel") || "");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);
  const [monthOffset, setMonthOffset] = useState(0);
  const [blocked, setBlocked] = useState(false);
  // Pago anticipado: métodos del salón + estado del pago del cliente
  const [payMethods, setPayMethods] = useState(null);
  const [payChoice, setPayChoice] = useState(""); // método elegido (o "in_salone")
  const [paySent, setPaySent] = useState(""); // "segnalato" | "in_salone"

  useEffect(() => {
    fetch(`${BACKEND}/api/settings/payment-methods`)
      .then((r) => r.json())
      .then(setPayMethods)
      .catch(() => setPayMethods(null));
  }, []);

  const reportPayment = async (method) => {
    if (!done?.booking?.id) return;
    try {
      const res = await fetch(`${BACKEND}/api/bookings/${done.booking.id}/pay-report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ method }),
      });
      const d = await res.json();
      if (res.ok) setPaySent(d.status);
    } catch {}
  };

  // Números bloqueados: no pueden usar NINGÚN botón de reserva (chequeo en vivo)
  useEffect(() => {
    const digits = telefono.replace(/\D/g, "");
    if (digits.length < 8) {
      setBlocked(false);
      return;
    }
    const t = setTimeout(() => {
      fetch(`${BACKEND}/api/check-phone`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ telefono }),
      })
        .then((r) => r.json())
        .then((d) => setBlocked(!!d.blocked))
        .catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [telefono]);

  // Días de descanso aplicables: del barbero elegido, o intersección si "primo disponibile"
  const offDays = useMemo(() => {
    if (!schedule.length) return new Set();
    if (barbiere) {
      const m = schedule.find((x) => x.nome === barbiere);
      return new Set(m ? m.days_off || [] : []);
    }
    const counts = {};
    schedule.forEach((m) => (m.days_off || []).forEach((d) => (counts[d] = (counts[d] || 0) + 1)));
    return new Set(
      Object.keys(counts)
        .filter((d) => counts[d] === schedule.length)
        .map(Number)
    );
  }, [schedule, barbiere]);

  const { first: monthFirst, cells } = useMemo(
    () => monthDays(monthOffset, offDays),
    [monthOffset, offDays]
  );

  useEffect(() => {
    fetch(`${BACKEND}/api/services`)
      .then((r) => r.json())
      .then((rows) => setServices(Array.isArray(rows) ? rows : []))
      .catch(() => setServices([]));
    fetch(`${BACKEND}/api/team/public`)
      .then((r) => r.json())
      .then((rows) => setTeam(Array.isArray(rows) ? rows : []))
      .catch(() => setTeam([]));
    fetch(`${BACKEND}/api/team/schedule`)
      .then((r) => r.json())
      .then((rows) => setSchedule(Array.isArray(rows) ? rows : []))
      .catch(() => setSchedule([]));
  }, []);

  useEffect(() => {
    if (!giorno) return;
    setSlotsLoading(true);
    setSlots(null);
    setOra("");
    fetch(
      `${BACKEND}/api/booking/slots?date=${giorno}&servizio=${encodeURIComponent(servizio)}&barbiere=${encodeURIComponent(barbiere)}`
    )
      .then((r) => r.json())
      .then((d) => setSlots(d.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [giorno, servizio, barbiere]);

  const submit = async () => {
    if (sending) return;
    setError("");
    setSending(true);
    try {
      const res = await fetch(`${BACKEND}/api/booking/self`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nome, telefono, servizio, giorno, ora, barbiere }),
      });
      const d = await res.json();
      if (!res.ok) {
        setError(d.detail || "Qualcosa è andato storto. Riprova.");
      } else {
        localStorage.setItem("bl_qb_nome", nome.trim());
        localStorage.setItem("bl_qb_tel", telefono.trim());
        setDone(d);
      }
    } catch {
      setError("Connessione assente. Controlla la rete e riprova.");
    }
    setSending(false);
  };

  const ready =
    !blocked &&
    nome.trim().length >= 2 &&
    telefono.replace(/\D/g, "").length >= 8 &&
    servizio && giorno && ora;

  return (
    <motion.div
      data-testid="quickbook-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-[80] flex items-end justify-center bg-zinc-950/70 backdrop-blur-sm"
    >
      <motion.div
        initial={{ y: 90 }}
        animate={{ y: 0 }}
        exit={{ y: 90 }}
        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="mx-auto flex max-h-[92dvh] w-full max-w-md flex-col overflow-hidden rounded-t-3xl border-t border-white/10 bg-zinc-900"
      >
        <div className="flex items-center justify-between px-6 pt-5">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">
              Prenotazione rapida
            </p>
            <h3 className="font-display text-xl font-bold text-white">
              {done ? "Fatto!" : "Prenota subito"}
            </h3>
          </div>
          <button
            data-testid="quickbook-close"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-white/5 text-zinc-400 transition-colors hover:bg-white/10 hover:text-white"
          >
            <X size={17} />
          </button>
        </div>

        <div className="flex flex-col gap-5 overflow-y-auto px-6 py-5 pb-10">
          {done ? (
            <div data-testid="quickbook-success" className="flex flex-col items-center gap-4 py-6 text-center">
              {done.status === "confermata" ? (
                <CheckCircle2 size={52} className="text-emerald-400" />
              ) : (
                <Hourglass size={52} className="text-amber-400" />
              )}
              <div>
                <p className="text-base font-extrabold text-white">
                  {done.status === "confermata"
                    ? "Prenotazione confermata!"
                    : "Richiesta in lista d'attesa"}
                </p>
                <p className="mt-1 text-sm text-zinc-400">
                  {done.booking.servizio} — {done.booking.giorno} alle {done.booking.ora}
                  {done.booking.barbiere ? ` · con ${done.booking.barbiere}` : ""}
                </p>
                <p className="mt-3 text-xs leading-relaxed text-zinc-500">
                  {done.status === "confermata"
                    ? "Salva il tuo promemoria qui sotto: lì vedi sempre lo stato della tua cita. Il barbiere ti scrive su WhatsApp per confermarti."
                    : "L'orario scelto era appena stato occupato: ti avvisiamo su WhatsApp se si libera."}
                </p>
              </div>

              {done.booking.id && (
                <a
                  data-testid="quickbook-reminder-link"
                  href={`/cita/${done.booking.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-amber-400/40 bg-amber-400/10 px-4 py-3 text-[11px] font-extrabold uppercase tracking-widest text-amber-300 transition-colors hover:bg-amber-400/20"
                >
                  📌 Il tuo promemoria — salva questo link
                </a>
              )}

              {payMethods && (
                <div
                  data-testid="quickbook-payment"
                  className="w-full rounded-2xl border border-white/10 bg-zinc-950/60 p-4 text-left"
                >
                  <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">
                    Metodi di pagamento — paga in anticipo
                  </p>
                  {paySent ? (
                    <p
                      data-testid="quickbook-pay-sent"
                      className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2.5 text-xs font-semibold text-emerald-300"
                    >
                      {paySent === "segnalato"
                        ? "Pagamento segnalato ✅ — il barbiere lo verificherà. Grazie!"
                        : "Perfetto: pagherai in salone il giorno della cita. 🤝"}
                    </p>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {payMethods.sumup && (
                        <button
                          data-testid="quickbook-pay-sumup"
                          onClick={() => {
                            window.open(payMethods.sumup, "_blank", "noopener,noreferrer");
                            setPayChoice("carta");
                          }}
                          className="flex items-center gap-3 rounded-xl border border-white/10 bg-zinc-950 px-4 py-3 text-left transition-colors hover:border-emerald-400/40"
                        >
                          <span className="text-lg">💳</span>
                          <span className="flex-1 text-sm font-bold text-white">Carta — SumUp</span>
                          <span className="text-[10px] font-bold uppercase text-emerald-400">Apri →</span>
                        </button>
                      )}
                      {payMethods.paypal && (
                        <button
                          data-testid="quickbook-pay-paypal"
                          onClick={() => {
                            window.open(payMethods.paypal, "_blank", "noopener,noreferrer");
                            setPayChoice("carta");
                          }}
                          className="flex items-center gap-3 rounded-xl border border-white/10 bg-zinc-950 px-4 py-3 text-left transition-colors hover:border-emerald-400/40"
                        >
                          <span className="text-lg">🅿️</span>
                          <span className="flex-1 text-sm font-bold text-white">PayPal</span>
                          <span className="text-[10px] font-bold uppercase text-emerald-400">Apri →</span>
                        </button>
                      )}
                      {payMethods.satispay && (
                        <button
                          data-testid="quickbook-pay-satispay"
                          onClick={() => {
                            window.open(payMethods.satispay, "_blank", "noopener,noreferrer");
                            setPayChoice("carta");
                          }}
                          className="flex items-center gap-3 rounded-xl border border-white/10 bg-zinc-950 px-4 py-3 text-left transition-colors hover:border-emerald-400/40"
                        >
                          <span className="text-lg">🔴</span>
                          <span className="flex-1 text-sm font-bold text-white">Satispay</span>
                          <span className="text-[10px] font-bold uppercase text-emerald-400">Apri →</span>
                        </button>
                      )}
                      {payMethods.iban && (
                        <button
                          data-testid="quickbook-pay-iban"
                          onClick={() => {
                            navigator.clipboard?.writeText(payMethods.iban).catch(() => {});
                            setPayChoice("carta");
                          }}
                          className="flex items-center gap-3 rounded-xl border border-white/10 bg-zinc-950 px-4 py-3 text-left transition-colors hover:border-emerald-400/40"
                        >
                          <span className="text-lg">🏦</span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-bold text-white">Bonifico</span>
                            <span className="block truncate text-[11px] text-zinc-400">{payMethods.iban}</span>
                          </span>
                          <span className="text-[10px] font-bold uppercase text-emerald-400">Copia</span>
                        </button>
                      )}
                      <button
                        data-testid="quickbook-pay-salone"
                        onClick={() => reportPayment("contanti")}
                        className="flex items-center gap-3 rounded-xl border border-white/10 bg-zinc-950 px-4 py-3 text-left transition-colors hover:border-amber-400/40"
                      >
                        <span className="text-lg">🤝</span>
                        <span className="flex-1 text-sm font-bold text-white">Pago in salone</span>
                      </button>
                      {payChoice === "carta" && (
                        <button
                          data-testid="quickbook-pay-confirm"
                          onClick={() => reportPayment("carta")}
                          className="mt-1 rounded-xl bg-emerald-500 py-3 text-[11px] font-extrabold uppercase tracking-widest text-white transition-colors hover:bg-emerald-400"
                        >
                          Ho pagato ✓
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              <button
                data-testid="quickbook-done-close"
                onClick={onClose}
                className="mt-2 rounded-xl bg-emerald-500 px-8 py-3 text-xs font-extrabold uppercase tracking-widest text-white transition-colors hover:bg-emerald-400"
              >
                Chiudi
              </button>
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-2">
                <span className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">
                  <Scissors size={12} className="text-amber-400" /> 1. Servizio
                </span>
                <div data-testid="quickbook-services" className="flex flex-wrap gap-2">
                  {services.map((s) => (
                    <button
                      key={s.id || s.nome}
                      data-testid={`quickbook-service-${s.nome}`}
                      onClick={() => !blocked && setServizio(s.nome)}
                      disabled={blocked}
                      className={`rounded-xl border px-3.5 py-2.5 text-left transition-[border-color,background-color,transform] duration-200 active:scale-[0.97] ${
                        servizio === s.nome
                          ? "border-emerald-400/60 bg-emerald-500/15"
                          : "border-white/10 bg-zinc-950 hover:border-white/25"
                      }`}
                    >
                      <span className="block text-sm font-bold text-white">{s.nome}</span>
                      <span className="block text-[11px] text-zinc-400">
                        {s.prezzo}€ · {s.durata} min
                      </span>
                    </button>
                  ))}
                  {services.length === 0 && (
                    <p className="text-xs text-zinc-500">Caricamento servizi…</p>
                  )}
                </div>
              </div>

              {team.length >= 2 && (
                <div className="flex flex-col gap-2">
                  <span className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">
                    <Users size={12} className="text-amber-400" /> 2. Barbiere
                  </span>
                  <div data-testid="quickbook-barbers" className="flex flex-wrap gap-2">
                    <button
                      data-testid="quickbook-barber-any"
                      onClick={() => !blocked && setBarbiere("")}
                      disabled={blocked}
                      className={`rounded-xl border px-3.5 py-2.5 text-sm font-bold transition-[border-color,background-color,transform] duration-200 active:scale-[0.97] ${
                        barbiere === ""
                          ? "border-emerald-400/60 bg-emerald-500/15 text-white"
                          : "border-white/10 bg-zinc-950 text-zinc-300 hover:border-white/25"
                      }`}
                    >
                      Il primo disponibile
                    </button>
                    {team.map((name) => (
                      <button
                        key={name}
                        data-testid={`quickbook-barber-${name}`}
                        onClick={() => !blocked && setBarbiere(name)}
                        disabled={blocked}
                        className={`rounded-xl border px-3.5 py-2.5 text-sm font-bold transition-[border-color,background-color,transform] duration-200 active:scale-[0.97] ${
                          barbiere === name
                            ? "border-emerald-400/60 bg-emerald-500/15 text-white"
                            : "border-white/10 bg-zinc-950 text-zinc-300 hover:border-white/25"
                        }`}
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                  {barbiere &&
                    (() => {
                      const m = schedule.find((x) => x.nome === barbiere);
                      return m && m.custom ? (
                        <p data-testid="quickbook-barber-hours" className="text-[11px] text-zinc-500">
                          Orari di {m.nome}: {m.morning_open}-{m.morning_close} e{" "}
                          {m.evening_open}-{m.evening_close}
                        </p>
                      ) : null;
                    })()}
                </div>
              )}

              <div className="flex flex-col gap-2">
                <span className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">
                  <CalendarDays size={12} className="text-amber-400" />{" "}
                  {team.length >= 2 ? "3" : "2"}. Giorno
                </span>
                <div className="rounded-2xl border border-white/10 bg-zinc-950 p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <button
                      data-testid="quickbook-month-prev"
                      onClick={() => setMonthOffset((o) => Math.max(0, o - 1))}
                      disabled={monthOffset === 0}
                      className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/5 text-zinc-300 transition-colors hover:bg-white/10 disabled:opacity-30"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <span
                      data-testid="quickbook-month-label"
                      className="text-sm font-extrabold capitalize text-white"
                    >
                      {IT_MESI[monthFirst.getMonth()]} {monthFirst.getFullYear()}
                    </span>
                    <button
                      data-testid="quickbook-month-next"
                      onClick={() => setMonthOffset((o) => Math.min(MAX_MONTHS_AHEAD, o + 1))}
                      disabled={monthOffset === MAX_MONTHS_AHEAD}
                      className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/5 text-zinc-300 transition-colors hover:bg-white/10 disabled:opacity-30"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                  <div className="mb-1 grid grid-cols-7 gap-1">
                    {WEEK_HEADER.map((w, i) => (
                      <span
                        key={i}
                        className="py-1 text-center text-[10px] font-bold uppercase text-zinc-600"
                      >
                        {w}
                      </span>
                    ))}
                  </div>
                  <div data-testid="quickbook-days" className="grid grid-cols-7 gap-1">
                    {cells.map((c, i) =>
                      c === null ? (
                        <span key={`x${i}`} />
                      ) : (
                        <button
                          key={c.iso}
                          data-testid={`quickbook-day-${c.iso}`}
                          onClick={() => !c.disabled && !blocked && setGiorno(c.iso)}
                          disabled={c.disabled || blocked}
                          className={`flex h-9 items-center justify-center rounded-lg text-sm font-bold transition-[background-color,color] duration-150 ${
                            giorno === c.iso
                              ? "bg-emerald-500 text-white"
                              : c.disabled
                                ? "text-zinc-700"
                                : "text-zinc-200 hover:bg-white/10"
                          }`}
                        >
                          {c.day}
                        </button>
                      )
                    )}
                  </div>
                  {giorno && (
                    <p data-testid="quickbook-day-selected" className="mt-2 text-center text-xs text-zinc-400">
                      {(() => {
                        const [y, m, dd] = giorno.split("-").map(Number);
                        const d = new Date(y, m - 1, dd);
                        return `${IT_GIORNI_FULL[d.getDay()]} ${dd} ${IT_MESI[m - 1]}`;
                      })()}
                    </p>
                  )}
                </div>
              </div>

              {giorno && (
                <div className="flex flex-col gap-2">
                  <span className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">
                    <Clock size={12} className="text-amber-400" />{" "}
                    {team.length >= 2 ? "4" : "3"}. Orario
                  </span>
                  {slotsLoading ? (
                    <p data-testid="quickbook-slots-loading" className="text-xs text-zinc-500">
                      Controllo disponibilità…
                    </p>
                  ) : slots && slots.length === 0 ? (
                    <p
                      data-testid="quickbook-slots-empty"
                      className="rounded-xl border border-white/10 bg-zinc-950 px-3 py-2.5 text-xs text-zinc-400"
                    >
                      Nessun orario libero in questo giorno. Prova un altro giorno.
                    </p>
                  ) : (
                    <div data-testid="quickbook-slots" className="grid grid-cols-4 gap-2">
                      {(slots || []).map((s) => (
                        <button
                          key={s}
                          data-testid={`quickbook-slot-${s}`}
                          onClick={() => !blocked && setOra(s)}
                          disabled={blocked}
                          className={`rounded-lg border py-2 text-sm font-bold transition-[border-color,background-color] duration-200 ${
                            ora === s
                              ? "border-emerald-400/60 bg-emerald-500 text-white"
                              : "border-white/10 bg-zinc-950 text-zinc-300 hover:border-white/25"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {ora && (
                <div className="flex flex-col gap-3">
                  <span className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">
                    <User size={12} className="text-amber-400" />{" "}
                    {team.length >= 2 ? "5" : "4"}. I tuoi dati
                  </span>
                  <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-zinc-950 px-3 focus-within:border-emerald-400/50">
                    <User size={15} className="shrink-0 text-zinc-500" />
                    <input
                      data-testid="quickbook-name-input"
                      value={nome}
                      onChange={(e) => setNome(e.target.value)}
                      placeholder="Il tuo nome"
                      maxLength={40}
                      className="h-11 min-w-0 flex-1 bg-transparent text-sm text-white outline-none placeholder:text-zinc-600"
                    />
                  </div>
                  <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-zinc-950 px-3 focus-within:border-emerald-400/50">
                    <Phone size={15} className="shrink-0 text-zinc-500" />
                    <input
                      data-testid="quickbook-phone-input"
                      value={telefono}
                      onChange={(e) => setTelefono(e.target.value)}
                      inputMode="tel"
                      placeholder="Numero WhatsApp (es. 3331234567)"
                      className="h-11 min-w-0 flex-1 bg-transparent text-sm text-white outline-none placeholder:text-zinc-600"
                    />
                  </div>
                  {blocked ? (
                    <p
                      data-testid="quickbook-blocked"
                      className="rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2.5 text-xs font-semibold leading-relaxed text-red-300"
                    >
                      Questo numero è bloccato: non è possibile prenotare online. Contatta
                      direttamente il salone.
                    </p>
                  ) : (
                    <p className="text-[11px] leading-relaxed text-zinc-500">
                      Il numero serve solo per mandarti la conferma su WhatsApp.
                    </p>
                  )}
                </div>
              )}

              {error && (
                <p
                  data-testid="quickbook-error"
                  className="rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs font-semibold text-red-300"
                >
                  {error}
                </p>
              )}

              <button
                data-testid="quickbook-submit"
                onClick={submit}
                disabled={!ready || sending}
                className="mt-1 rounded-2xl bg-emerald-500 py-4 text-sm font-extrabold uppercase tracking-widest text-white shadow-[0_0_24px_rgba(16,185,129,0.25)] transition-[background-color,transform] duration-200 hover:bg-emerald-400 active:scale-[0.97] disabled:opacity-40 disabled:shadow-none"
              >
                {sending ? "Invio in corso…" : "Conferma prenotazione"}
              </button>
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
